import React from "react";
import styled, { keyframes } from "styled-components";
import dayjs from "dayjs";
import "dayjs/locale/ko";

import { HelpIcon } from "assets/icons/system";
import { UserIcon as DashboardUserIcon } from "assets/icons/businessDashboard";

dayjs.locale("ko");

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const Skeleton = styled.div<any>`
  background: #e0e0e0;
  background-image: linear-gradient(
    90deg,
    #e0e0e0 0px,
    #f5f5f5 40px,
    #e0e0e0 80px
  );
  background-size: 400px 100%;
  animation: ${shimmer} 1.2s infinite linear;
  border-radius: ${({ rounded }) => (rounded ? "9999px" : "8px")};
  width: ${({ width }) => width || "100%"};
  height: ${({ height }) => height || "20px"};
  margin: ${({ margin }) => margin || "0"};
`;

const SectionWrapper = styled.div`
  padding: 24px;
  background-color: #f3f7ff;
  position: relative;
`;

const BackColor = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 194px;
  background-color: #0f2859;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  position: relative;
  margin-bottom: 16px;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  line-height: 28px;
  letter-spacing: -0.4px;
  font-weight: bold;
  color: #fff;
`;

const SectionTime = styled.p`
  font-size: 12px;
  font-weight: bold;
  line-height: 14px;
  letter-spacing: -0.2px;
  color: #949db2;
  margin: 0;
`;

const HelpButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  cursor: pointer;
  background: none;
  border: none;
  transition: color 0.2s;
  &:hover {
    color: #fff;
  }
`;

const StyledCard = styled.div`
  padding: 0;
  border-radius: 16px;
  border: none;
  box-shadow: none;
  background: #fff;
  z-index: 10;
  position: relative;
`;

const StyledCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 12px;
`;

const PieChartSkeletonInner = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 230px;
`;

const PieChartSkeletonWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  max-width: 396px;
`;

const PieChartCenter = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.7rem;
`;

const BreakdownCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 12px;
`;

const BreakdownCardSkeletonBox = styled.div`
  background: #fff;
  padding: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 96px;
`;

const BreakdownCardSkeletonInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export const RealtimeMembersSectionSkeleton = () => {
  return (
    <SectionWrapper>
      <BackColor />
      <SectionHeader>
        <DashboardUserIcon />
        <HeaderContent>
          <SectionTitle>실시간 유효 회원</SectionTitle>
          <HelpButton type="button">
            <HelpIcon />
          </HelpButton>
          <SectionTime>오늘 {dayjs().format("HH:mm")} 기준</SectionTime>
        </HeaderContent>
      </SectionHeader>

      <StyledCard>
        <StyledCardContent>
          <PieChartSkeletonWrap>
            <PieChartSkeletonInner>
              <Skeleton width="220px" height="220px" rounded />
              <PieChartCenter>
                <Skeleton width="112px" height="40px" />
                <Skeleton width="64px" height="24px" />
              </PieChartCenter>
            </PieChartSkeletonInner>
          </PieChartSkeletonWrap>
          <BreakdownCardsGrid>
            {[1, 2, 3, 4].map((_, i) => (
              <MembersBreakdownCardSkeleton key={i} />
            ))}
          </BreakdownCardsGrid>
        </StyledCardContent>
      </StyledCard>
    </SectionWrapper>
  );
};

const MembersBreakdownCardSkeleton = () => (
  <BreakdownCardSkeletonBox>
    <BreakdownCardSkeletonInfo>
      <Skeleton width="32px" height="20px" />
      <Skeleton width="64px" height="28px" />
    </BreakdownCardSkeletonInfo>
    <Skeleton width="60px" height="60px" rounded />
  </BreakdownCardSkeletonBox>
);
